/**
 * Configuration Sentry pour monitoring erreurs production
 * Enterprise-grade error tracking et performance monitoring
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

export function initSentry() {
  // Ne pas initialiser Sentry en développement local
  if (ENVIRONMENT === 'development' || !SENTRY_DSN) {
    console.log('🔍 Sentry désactivé en développement');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,

    // Environment
    environment: ENVIRONMENT,
    release: `nocodeo@${APP_VERSION}`,

    // Integrations
    integrations: [
      // Performance monitoring - browserTracingIntegration is part of @sentry/react in v8+
      Sentry.browserTracingIntegration({
        // Tracer toutes les requêtes fetch/XHR
        traceFetch: true,
        traceXHR: true,
      }),

      // Replay sessions pour debug
      Sentry.replayIntegration({
        maskAllText: true, // RGPD - masquer tout le texte
        blockAllMedia: true, // RGPD - bloquer média
      }),

      // Capture des logs console
      Sentry.captureConsoleIntegration({
        levels: ['error', 'warn'], // Capturer les erreurs et warnings
      }),
    ],

    // Trace propagation targets (moved from browserTracingIntegration in Sentry v8+)
    tracePropagationTargets: [
      'localhost',
      'nocodeo.fr',
      /^\//,
    ],

    // Replay sampling rates
    replaysSessionSampleRate: 0, // 0% des sessions normales
    replaysOnErrorSampleRate: 1.0, // 100% des sessions avec erreurs

    // Performance Monitoring
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0, // 10% en prod, 100% en staging

    // Filtrage des erreurs
    beforeSend(event, hint) {
      // Ignorer les erreurs réseau connues
      const error = hint.originalException as Error;

      if (error?.message) {
        // Ignorer les erreurs de connexion réseau
        if (error.message.includes('NetworkError') ||
            error.message.includes('Failed to fetch')) {
          return null;
        }

        // Ignorer les erreurs d'extension navigateur
        if (error.message.includes('chrome-extension://') ||
            error.message.includes('moz-extension://')) {
          return null;
        }
      }

      return event;
    },

    // Ignore des erreurs par pattern
    ignoreErrors: [
      // Erreurs réseau
      'NetworkError',
      'Failed to fetch',
      'Load failed',

      // Erreurs extensions navigateur
      /chrome-extension/i,
      /moz-extension/i,

      // Erreurs script tiers
      'ResizeObserver loop',
      'Non-Error promise rejection',
    ],

    // Breadcrumbs pour debug
    beforeBreadcrumb(breadcrumb, hint) {
      // Capturer tous les breadcrumbs console maintenant que Logs est activé
      return breadcrumb;
    },
  });

  console.log('✅ Sentry initialisé', {
    environment: ENVIRONMENT,
    version: APP_VERSION,
  });
}

/**
 * Capturer une erreur manuellement
 */
export function captureError(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capturer un message
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Ajouter du contexte utilisateur
 */
export function setUserContext(user: {
  id: string;
  email?: string;
  role?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
  });
}

/**
 * Supprimer le contexte utilisateur (logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Ajouter des tags personnalisés
 */
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * Ajouter un breadcrumb manuel
 */
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
  });
}

/**
 * Démarrer une transaction de performance
 */

// Export de Sentry pour usage avancé
export { Sentry };
/**
 * Démarrer une transaction de performance
 * Note: startTransaction is deprecated in Sentry v8+, use startSpan instead
 */
export function startTransaction(name: string, op: string = 'custom') {
  // Use startSpan for Sentry v8+ compatibility
  return Sentry.startSpan(
    {
      name,
      op,
    },
    (span) => span
  );
}
